/**
 * Cart Drawer Extensions — Celebrity Attire Dawn theme.
 *
 * Custom Web Components used inside the cart drawer:
 *   <cart-discount-field>  — AJAX discount code apply / remove
 *
 * Loaded via defer in snippets/cart-drawer.liquid.
 */

/* -------------------------------------------------------------------------- */
/*  <cart-discount-field>                                                     */
/*  Wraps the discount code input + apply button inside snippets/cart-discount */
/*  Handles: apply via /discount/CODE, remove via /discount/, section re-render */
/* -------------------------------------------------------------------------- */

class CartDiscountField extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.cart-discount__input');
    this.applyBtn = this.querySelector('.cart-discount__apply');
    this.loader = this.querySelector('.cart-discount__loader');
    this.discountWrap = this.closest('.cart-discount');

    /* Apply button — delegated click */
    this.addEventListener('click', (event) => {
      const btn = event.target.closest('.cart-discount__apply');
      if (!btn) return;
      this.applyDiscount();
    });

    /* Enter key in the input field */
    this.input?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.applyDiscount();
      }
    });

    /* Remove buttons — delegated to the parent .cart-discount container */
    this.discountWrap?.addEventListener('click', (event) => {
      const removeBtn = event.target.closest('.cart-discount__remove');
      if (!removeBtn) return;
      this.removeDiscount();
    });
  }

  /* ---- Apply ------------------------------------------------------------ */

  async applyDiscount() {
    const code = this.input?.value.trim();
    if (!code) return;

    this.setLoading(true);
    this.clearError();

    try {
      /* Set the discount code in the Shopify session */
      await fetch(`/discount/${encodeURIComponent(code)}`);

      /* Fetch fresh section HTML */
      const sections = await this.fetchSections();
      const drawerDoc = this.parseSection(sections['cart-drawer']);

      /* Check if the code actually appears in the applied-discounts area */
      const appliedEl = drawerDoc.querySelector('.cart-discount__applied');
      const wasApplied = appliedEl?.textContent.toLowerCase().includes(code.toLowerCase()) ?? false;

      /* Replace drawer + icon bubble with fresh HTML */
      this.replaceDrawer(drawerDoc);
      this.replaceBubble(sections);

      /* If the code wasn't applied, show an error on the freshly-rendered elements */
      if (!wasApplied) {
        const freshBanner = document.querySelector('.cart-discount__banner');
        const freshError = freshBanner?.querySelector('.cart-discount__error');
        if (freshBanner && freshError) {
          freshError.textContent = 'Invalid or expired discount code';
          freshBanner.hidden = false;
        }
      }
    } catch {
      /* Network / parse error — current elements are still in the DOM */
      this.showError('Unable to apply discount. Please try again.');
      this.setLoading(false);
    }
  }

  /* ---- Remove ----------------------------------------------------------- */

  async removeDiscount() {
    this.setLoading(true);

    try {
      /* Clear the discount from the Shopify session */
      await fetch('/discount/');

      const sections = await this.fetchSections();
      const drawerDoc = this.parseSection(sections['cart-drawer']);

      this.replaceDrawer(drawerDoc);
      this.replaceBubble(sections);
    } catch {
      this.showError('Unable to remove discount. Please try again.');
      this.setLoading(false);
    }
  }

  /* ---- Section Rendering helpers ---------------------------------------- */

  async fetchSections() {
    const cartUrl = window.routes?.cart_url ?? '/cart';
    const response = await fetch(`${cartUrl}?sections=cart-drawer,cart-icon-bubble`);
    if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);
    return response.json();
  }

  parseSection(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  replaceDrawer(doc) {
    const freshInner = doc.querySelector('.drawer__inner');
    const currentInner = document.querySelector('cart-drawer .drawer__inner');
    if (freshInner && currentInner) {
      currentInner.innerHTML = freshInner.innerHTML;
    }
  }

  replaceBubble(sections) {
    const bubbleHtml = sections['cart-icon-bubble'];
    if (!bubbleHtml) return;
    const doc = this.parseSection(bubbleHtml);
    const freshBubble = doc.querySelector('.shopify-section');
    const currentBubble = document.getElementById('cart-icon-bubble');
    if (freshBubble && currentBubble) {
      currentBubble.innerHTML = freshBubble.innerHTML;
    }
  }

  /* ---- UI state helpers ------------------------------------------------- */

  setLoading(active) {
    if (this.loader) this.loader.hidden = !active;
    if (this.applyBtn) this.applyBtn.disabled = active;
    if (this.input) this.input.disabled = active;
  }

  showError(message) {
    const banner = this.discountWrap?.querySelector('.cart-discount__banner');
    const errorEl = banner?.querySelector('.cart-discount__error');
    if (banner && errorEl) {
      errorEl.textContent = message;
      banner.hidden = false;
    }
  }

  clearError() {
    const banner = this.discountWrap?.querySelector('.cart-discount__banner');
    if (banner) banner.hidden = true;
  }
}

if (!customElements.get('cart-discount-field')) {
  customElements.define('cart-discount-field', CartDiscountField);
}

/* -------------------------------------------------------------------------- */
/*  <free-shipping-bar>                                                       */
/*  Reads threshold & total-price attributes, calculates progress, updates    */
/*  the fill bar width and text message. Re-evaluates on theme:cart:updated.  */
/* -------------------------------------------------------------------------- */

class FreeShippingBar extends HTMLElement {
  connectedCallback() {
    this.threshold = parseInt(this.getAttribute('threshold'), 10) || 0;
    this.totalPrice = parseInt(this.getAttribute('total-price'), 10) || 0;
    this.reachedMessage = this.getAttribute('reached-message') ?? 'You\'ve unlocked free shipping!';
    this.unreachedMessage = this.getAttribute('unreached-message') ?? 'Add [[amount]] more for free shipping';
    this.textEl = this.querySelector('.free-shipping-bar__text');
    this.fillEl = this.querySelector('.free-shipping-bar__fill');

    this.update(this.totalPrice);

    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    document.addEventListener('theme:cart:updated', this.handleCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('theme:cart:updated', this.handleCartUpdate);
  }

  handleCartUpdate(event) {
    const { cart } = event.detail ?? {};
    if (!cart) return;

    /* Recalculate shipping-eligible total */
    let shippingTotal = 0;
    for (const item of cart.items ?? []) {
      if (item.requires_shipping) {
        shippingTotal += item.final_line_price;
      }
    }
    this.update(shippingTotal);
  }

  update(total) {
    if (this.threshold <= 0) return;

    const progress = Math.min(100, Math.round((total / this.threshold) * 100));
    const reached = total >= this.threshold;

    if (this.fillEl) {
      this.fillEl.classList.toggle('is-full', reached);
      /* Width set via CSS custom property to avoid inline styles */
      this.fillEl.style.setProperty('--shipping-progress', `${progress}%`);
    }

    if (this.textEl) {
      if (reached) {
        this.textEl.textContent = this.reachedMessage;
      } else {
        const remaining = this.threshold - total;
        /* Format remaining as currency — use Shopify's money format from window */
        const formatted = this.formatMoney(remaining);
        this.textEl.textContent = this.unreachedMessage.replace('[[amount]]', formatted);
      }
    }

    this.classList.toggle('is-reached', reached);
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    /* Use store's currency symbol from Shopify global */
    const symbol = window.Shopify?.currency?.symbol ?? '₹';
    return `${symbol}${amount}`;
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
